import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router';
import { Shield, Loader2, AlertTriangle, CheckCircle, XCircle } from 'lucide-react';
import { createClient } from '@/lib/supabase';

interface AuthorizationDetails {
  client: {
    name: string;
    icon_uri?: string;
    uri?: string;
  };
  redirect_uri: string;
  scopes?: string[];
}

const SCOPE_LABELS: Record<string, string> = {
  openid: 'Verify your identity',
  email: 'View your email address',
  profile: 'View your profile information',
};

export function OAuthConsentPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const authorizationId = searchParams.get('authorization_id');

  const [authDetails, setAuthDetails] = useState<AuthorizationDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [deciding, setDeciding] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function loadAuthDetails() {
      if (!authorizationId) {
        setError('Missing authorization_id parameter.');
        setLoading(false);
        return;
      }

      const supabase = createClient();

      // Check if user is authenticated
      const {
        data: { user },
      } = await supabase.auth.getUser();

      if (!user) {
        // Redirect to login, preserving authorization_id so we come back here
        navigate(
          `/login?redirect=${encodeURIComponent(`/oauth/consent?authorization_id=${authorizationId}`)}`,
          { replace: true },
        );
        return;
      }

      // Get authorization details
      const { data, error: authError } =
        await (supabase.auth as any).oauth.getAuthorizationDetails(authorizationId);

      if (authError || !data) {
        setError(authError?.message || 'Invalid or expired authorization request.');
        setLoading(false);
        return;
      }

      setAuthDetails(data);
      setLoading(false);
    }

    loadAuthDetails();
  }, [authorizationId, navigate]);

  async function handleApprove() {
    if (!authorizationId) return;
    setDeciding(true);
    setError(null);

    const supabase = createClient();
    const { data, error: approveError } =
      await (supabase.auth as any).oauth.approveAuthorization(authorizationId);

    if (approveError) {
      setError(approveError.message);
      setDeciding(false);
      return;
    }

    // Redirect back to the requesting app with the authorization code
    const redirectUrl = data?.redirect_url ?? data?.redirect_to;
    if (!redirectUrl) {
      console.error('approveAuthorization response:', JSON.stringify(data));
      setError(`Unexpected response from authorization server. Check browser console for details.`);
      setDeciding(false);
      return;
    }
    window.location.href = redirectUrl;
  }

  async function handleDeny() {
    if (!authorizationId) return;
    setDeciding(true);
    setError(null);

    const supabase = createClient();
    const { data, error: denyError } =
      await (supabase.auth as any).oauth.denyAuthorization(authorizationId);

    if (denyError) {
      setError(denyError.message);
      setDeciding(false);
      return;
    }

    // Redirect back to the requesting app with error
    const redirectUrl = data?.redirect_url ?? data?.redirect_to;
    if (!redirectUrl) {
      console.error('denyAuthorization response:', JSON.stringify(data));
      setError(`Unexpected response from authorization server. Check browser console for details.`);
      setDeciding(false);
      return;
    }
    window.location.href = redirectUrl;
  }

  // ── Loading state ──────────────────────────────────────────────

  if (loading) {
    return (
      <div className="min-h-screen bg-base-100 flex items-center justify-center dot-grid">
        <div className="glass-card rounded-2xl p-8 w-full max-w-sm text-center space-y-4 animate-fade-in">
          <Loader2 size={32} className="animate-spin mx-auto text-primary" />
          <p className="text-sm text-base-content/50">Loading authorization request...</p>
        </div>
      </div>
    );
  }

  // ── Error state ────────────────────────────────────────────────

  if (error && !authDetails) {
    return (
      <div className="min-h-screen bg-base-100 flex items-center justify-center dot-grid">
        <div className="glass-card rounded-2xl p-8 w-full max-w-sm text-center space-y-4 animate-fade-in">
          <AlertTriangle size={32} className="mx-auto text-error" />
          <p className="text-sm text-error">{error}</p>
          <Link to="/dashboard" className="btn btn-primary btn-sm">
            Go to Dashboard
          </Link>
        </div>
      </div>
    );
  }

  // ── Consent screen ─────────────────────────────────────────────

  const clientName = authDetails?.client.name || 'Unknown Application';
  const scopes = authDetails?.scopes ?? [];

  return (
    <div className="min-h-screen bg-base-100 flex items-center justify-center dot-grid relative">
      <div className="glass-card rounded-2xl p-8 w-full max-w-md space-y-6 animate-fade-in">
        {/* Header */}
        <div className="text-center space-y-2">
          <div className="text-3xl font-extrabold tracking-tight">
            Hy<span className="text-primary">Pe</span>Ma
          </div>
          <p className="text-[8px] font-semibold uppercase tracking-[0.15em] text-base-content/30">
            Hyper Personalized Mail
          </p>
        </div>

        {/* Permission request */}
        <div className="text-center space-y-2">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-primary/10">
            <Shield size={24} className="text-primary" />
          </div>
          <h1 className="text-lg font-semibold text-base-content">
            Authorize {clientName}
          </h1>
          <p className="text-sm text-base-content/60">
            <strong>{clientName}</strong> wants to access your HyPeMa account.
          </p>
        </div>

        {/* Scopes */}
        {scopes.length > 0 && (
          <div className="bg-base-200/50 rounded-xl p-4 space-y-2">
            <p className="text-xs font-semibold uppercase tracking-wider text-base-content/40">
              This will allow the app to:
            </p>
            <ul className="space-y-1.5">
              {scopes.map((scope) => (
                <li key={scope} className="flex items-center gap-2 text-sm text-base-content/80">
                  <CheckCircle size={14} className="text-success shrink-0" />
                  {SCOPE_LABELS[scope] || scope}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* What this means */}
        <div className="bg-base-200/50 rounded-xl p-4 space-y-2">
          <p className="text-xs font-semibold uppercase tracking-wider text-base-content/40">
            The app will be able to:
          </p>
          <ul className="space-y-1.5 text-sm text-base-content/80">
            <li className="flex items-center gap-2">
              <CheckCircle size={14} className="text-success shrink-0" />
              Research companies and generate leads
            </li>
            <li className="flex items-center gap-2">
              <CheckCircle size={14} className="text-success shrink-0" />
              Create and send personalized emails
            </li>
            <li className="flex items-center gap-2">
              <CheckCircle size={14} className="text-success shrink-0" />
              View and manage your leads
            </li>
            <li className="flex items-center gap-2">
              <CheckCircle size={14} className="text-success shrink-0" />
              Check your token balance
            </li>
          </ul>
        </div>

        {/* Error */}
        {error && (
          <div className="text-sm text-error bg-error/10 rounded-lg px-3 py-2">
            {error}
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-3">
          <button
            onClick={handleDeny}
            disabled={deciding}
            className="btn btn-outline flex-1"
          >
            {deciding ? <Loader2 size={16} className="animate-spin" /> : <XCircle size={16} />}
            Deny
          </button>
          <button
            onClick={handleApprove}
            disabled={deciding}
            className="btn btn-primary flex-1"
          >
            {deciding ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle size={16} />}
            Approve
          </button>
        </div>

        <p className="text-xs text-base-content/30 text-center">
          You can revoke access at any time in your{' '}
          <Link to="/settings" className="underline hover:text-base-content/50">
            settings
          </Link>
          .
        </p>
      </div>
    </div>
  );
}
